import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { BaseUrlFault } from '../lib/keyCapture';
import type { ProbeRefusal } from '../lib/keyProbeRefusal';
import { baseUrlHintKey } from '../lib/providerEndpoint';
import { Field } from './Field';
import { providerLabel } from './KeyCard';
import { ProviderSelect } from './ProviderSelect';
import { Button, buttonClass } from './ui/Button';

// Phase 1 of <AddKeyModal/> (arc 101 slice 02): the CREDENTIAL — provider,
// endpoint override, secret. Presenter; `createKeyCapture` owns the state and
// the round trip.
//
// There is no model field here on purpose: not knowing the id is the reason the
// enumeration exists, and the free-text box this replaced is what put
// `gpt-3.5-turbo`, `kimi-k2`, `glm-4.6` and `glm-4.7` on live prod rows.
//
// The provider list is `<ProviderSelect/>`'s grouped picklist with `allowAny`
// OFF — blank names no endpoint to dial, so the option is not rendered rather
// than refused after the fact. The key stays a password input and lives in the
// capture controller's local only until it is POSTed; nothing renders it back.
//
// The endpoint field speaks for itself: a provider whose endpoint is not
// guessable (alibaba's is workspace-scoped) carries a hint naming the shapes, and
// a required endpoint left empty — or a value that is not a URL — answers on the
// FIELD, before any request. The input's own `invalid` event is caught (and the
// browser's bubble suppressed) so a real browser and happy-dom render the same
// sentence; the controller catches the case constraint validation cannot see.

export type AddKeyCredentialStepProps = {
  readonly provider: string;
  readonly onProvider: (value: string) => void;
  readonly baseUrl: string;
  readonly onBaseUrl: (value: string) => void;
  readonly wantsBaseUrl: boolean;
  readonly needsBaseUrl: boolean;
  /** The endpoint field's own refusal, or null while none stands. */
  readonly baseUrlFault: BaseUrlFault | null;
  /** The input's constraint validation fired — which constraint failed. */
  readonly onBaseUrlInvalid: (fault: BaseUrlFault) => void;
  readonly secret: string;
  readonly onSecret: (value: string) => void;
  /** An enumeration in flight — the submit reads "Listing models…". */
  readonly probing: boolean;
  readonly incomplete: boolean;
  /** The last refusal, framed by `probeRefusal`. Null while none stands. */
  readonly refusal: ProbeRefusal | null;
  readonly onCancel: () => void;
};

const FAULT_KEY: Readonly<Record<BaseUrlFault, string>> = {
  missing: 'account_keys.base_url_missing',
  malformed: 'account_keys.base_url_malformed',
};

export function AddKeyCredentialStep(props: AddKeyCredentialStepProps): JSX.Element {
  const hint = (): string | undefined => {
    const key = baseUrlHintKey(props.provider);
    return key === null ? undefined : t(key);
  };
  return (
    <>
      <div class="field-group">
        <p class="field__desc">{t('account_keys.credential_desc')}</p>
        <Field label={t('account_keys.provider')} id="add-key-provider">
          {(a11y) => (
            <ProviderSelect
              {...a11y}
              label={t('account_keys.provider')}
              value={props.provider}
              onChange={props.onProvider}
              allowAny={false}
            />
          )}
        </Field>
        <Show when={props.wantsBaseUrl}>
          <Field
            label={t('account_keys.base_url')}
            id="add-key-base-url"
            desc={hint()}
            error={
              props.baseUrlFault === null
                ? null
                : t(FAULT_KEY[props.baseUrlFault], { provider: providerLabel(props.provider) })
            }
          >
            {(a11y) => (
              <input
                {...a11y}
                class="input"
                type="url"
                value={props.baseUrl}
                onInput={(e) => props.onBaseUrl(e.currentTarget.value)}
                placeholder={t('account_keys.base_url_placeholder')}
                required={props.needsBaseUrl}
                onInvalid={(e) => {
                  e.preventDefault();
                  props.onBaseUrlInvalid(
                    e.currentTarget.validity.valueMissing ? 'missing' : 'malformed',
                  );
                }}
              />
            )}
          </Field>
        </Show>
        <Field label={t('account_keys.key')} id="add-key-secret">
          {(a11y) => (
            <input
              {...a11y}
              class="input"
              type="password"
              value={props.secret}
              onInput={(e) => props.onSecret(e.currentTarget.value)}
              placeholder={t('account_keys.key_placeholder')}
              autocomplete="off"
              required
            />
          )}
        </Field>
      </div>
      {/* The framing line names the CLASS of refusal (branching on the problem's
          `reason`, never its prose); the server's own sentence goes underneath,
          because it is the half that names the endpoint or the provider status. */}
      <Show when={props.refusal}>
        {(refusal) => (
          <div class="field__error" role="alert">
            <p>{t(refusal().key, refusal().vars)}</p>
            <Show when={refusal().detail}>
              {(detail) => <p class="add-key__refusal-detail">{detail()}</p>}
            </Show>
          </div>
        )}
      </Show>
      <div class="dialog__footer">
        <Button variant="outline" onClick={props.onCancel}>
          {t('account_keys.cancel')}
        </Button>
        <button type="submit" class={buttonClass()} disabled={props.probing || props.incomplete}>
          {props.probing ? t('account_keys.listing_models') : t('account_keys.list_models')}
        </button>
      </div>
    </>
  );
}
